(() => {
  if (customElements.get('real-sale-collection')) return;

  class RealSaleCollection extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.requests = new Map();
      this.tabs = [...this.querySelectorAll('[data-sale-tab]')];
      this.panels = [...this.querySelectorAll('[data-sale-panel]')];
      if (!this.tabs.length || !this.panels.length) return;

      const signal = this.controller.signal;
      this.addEventListener('click', this.onClick, { signal });
      this.addEventListener('keydown', this.onKeydown, { signal });
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
      const tablist = this.querySelector('[data-tablist]');
      const fallback = this.querySelector('[data-fallback-nav]');
      if (tablist) tablist.hidden = false;
      if (fallback) fallback.hidden = true;
      this.dataset.enhanced = 'true';

      const initialIndex = Math.max(0, Math.min(this.tabs.length - 1, Number(this.dataset.defaultTab || 1) - 1));
      this.select(this.tabs[initialIndex]);
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    panelFor(tab) {
      return this.panels.find((panel) => panel.id === (tab.dataset.panelId || tab.getAttribute('aria-controls')));
    }

    onClick = (event) => {
      const tab = event.target.closest('[data-sale-tab]');
      if (tab && this.contains(tab)) this.select(tab);
      const retry = event.target.closest('[data-retry]');
      if (retry && this.contains(retry)) {
        const panel = retry.closest('[data-sale-panel]');
        if (panel) this.load(panel);
      }
    };

    onKeydown = (event) => {
      const tab = event.target.closest('[data-sale-tab]');
      if (!tab || !this.contains(tab)) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let index = this.tabs.indexOf(tab);
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.tabs.length - 1;
      else index = (index + (event.key === 'ArrowRight' ? direction : -direction) + this.tabs.length) % this.tabs.length;
      this.tabs[index].focus({ preventScroll: true });
      this.select(this.tabs[index]);
    };

    onBlockSelect = (event) => {
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      const panel = event.target.closest('[data-sale-panel]');
      const tab = event.target.closest('[data-sale-tab]') || this.tabs.find((candidate) => this.panelFor(candidate) === panel);
      if (tab) this.select(tab);
    };

    select(tab) {
      const activePanel = this.panelFor(tab);
      if (!activePanel) return;
      this.tabs.forEach((candidate) => {
        const active = candidate === tab;
        candidate.setAttribute('aria-selected', String(active));
        candidate.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => { panel.hidden = panel !== activePanel; });
      this.revealTab(tab);
      if (activePanel.dataset.needsFeed === 'true' && activePanel.dataset.loaded !== 'true') {
        this.load(activePanel);
      }
    }

    revealTab(tab) {
      const tablist = this.querySelector('[data-tablist]');
      if (!tablist || tablist.scrollWidth <= tablist.clientWidth) return;
      if (!tablist.scrollBy || !tab.getBoundingClientRect || !tablist.getBoundingClientRect) return;
      const viewport = tablist.getBoundingClientRect();
      const target = tab.getBoundingClientRect();
      const inset = 8;
      let delta = 0;
      if (target.left < viewport.left + inset) delta = target.left - viewport.left - inset;
      else if (target.right > viewport.right - inset) delta = target.right - viewport.right + inset;
      if (delta) tablist.scrollBy({ left: delta, behavior: 'auto' });
    }

    status(panel, message, retry = false) {
      const status = panel.querySelector('[data-status]');
      const retryButton = panel.querySelector('[data-retry]');
      if (status) {
        status.textContent = message;
        status.hidden = !message;
      }
      if (retryButton) retryButton.hidden = !retry;
    }

    load(panel) {
      if (this.requests.has(panel)) return this.requests.get(panel);
      const requests = this.requests;
      const operation = this.fetchProducts(panel).finally(() => requests.delete(panel));
      requests.set(panel, operation);
      return operation;
    }

    namespaceFeedCard(item, prefix) {
      const elements = [item, ...item.querySelectorAll('*')];
      const ids = new Map();
      elements.forEach((element) => {
        const id = element.getAttribute('id');
        if (!id) return;
        const next = `${prefix}${id}`;
        ids.set(id, next);
        element.setAttribute('id', next);
      });
      if (!ids.size) return;

      const references = new Set([
        'for', 'form', 'list', 'headers', 'popovertarget', 'commandfor',
        'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns',
        'aria-activedescendant', 'aria-errormessage',
      ]);
      elements.forEach((element) => {
        element.getAttributeNames().forEach((name) => {
          const value = element.getAttribute(name);
          if (references.has(name)) {
            element.setAttribute(name, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
          } else if ((name === 'href' || name === 'xlink:href') && value.startsWith('#')) {
            const id = ids.get(value.slice(1));
            if (id) element.setAttribute(name, `#${id}`);
          } else if (value.includes('url(#')) {
            element.setAttribute(name, value.replace(/url\(#([^)]*)\)/g, (reference, id) => (
              ids.has(id) ? `url(#${ids.get(id)})` : reference
            )));
          }
        });
      });
    }

    async fetchProducts(panel) {
      const lifecycle = this.controller;
      const requestController = new AbortController();
      const abort = () => requestController.abort();
      lifecycle.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 12000);
      panel.setAttribute('aria-busy', 'true');
      this.status(panel, this.dataset.loadingMessage);

      try {
        const url = new URL(panel.dataset.feedUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid collection source');
        url.searchParams.set('section_id', 'real-sale-product-feed');
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);

        const response = await fetch(url, {
          signal: requestController.signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Collection request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = document.querySelector('[data-sale-feed] [data-feed-products]');
        if (!feed) throw new Error('Missing collection content');
        const usedOnly = panel.dataset.usedOnly === 'true';
        const maxProducts = Math.max(1, Math.min(12, Number(panel.dataset.maxProducts || this.dataset.maxProducts || 4)));
        const items = [...feed.children]
          .filter((item) => item.matches('li[data-product-condition]'))
          .filter((item) => !usedOnly || item.dataset.productCondition === 'Used')
          .slice(0, maxProducts);
        if (!this.isConnected || lifecycle.signal.aborted) return;

        const products = panel.querySelector('[data-products]');
        if (!products) throw new Error('Missing product list');
        items.forEach((item, index) => {
          this.namespaceFeedCard(item, `${panel.id}-feed-${index}-`);
        });
        products.replaceChildren(...items);
        panel.dataset.loaded = 'true';
        this.status(panel, items.length ? '' : this.dataset.emptyMessage);
      } catch (error) {
        if (!this.isConnected || lifecycle.signal.aborted) return;
        this.status(panel, this.dataset.errorMessage, true);
      } finally {
        clearTimeout(timeout);
        lifecycle.signal.removeEventListener('abort', abort);
        if (this.isConnected && !lifecycle.signal.aborted) panel.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('real-sale-collection', RealSaleCollection);
})();
